import { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Decision, 
  DecisionFilter, 
  SyncStatus
} from '../types/audit-trail';
import { offlineManager } from '../lib/audit-trail/offline-manager';
import { decisionCrypto } from '../lib/audit-trail/decision-crypto';
import { validationService } from '../lib/audit-trail/validation';

interface UseDecisionsOptions {
  autoSync?: boolean;
  syncInterval?: number; // minutes
  enableOffline?: boolean;
}

interface UseDecisionsReturn {
  // State
  decisions: Decision[];
  loading: boolean;
  error: string | null;
  syncStatus: SyncStatus | null;
  pendingSyncCount: number;
  
  // Actions
  createDecision: (decision: Omit<Decision, 'id' | 'createdAt' | 'updatedAt' | 'signature'>) => Promise<Decision>;
  updateDecision: (id: string, updates: Partial<Decision>) => Promise<Decision>;
  deleteDecision: (id: string) => Promise<void>;
  getDecision: (id: string) => Promise<Decision | null>;
  
  // List operations
  loadDecisions: (filter?: DecisionFilter) => Promise<void>;
  refreshDecisions: () => Promise<void>;
  searchDecisions: (searchTerm: string) => Promise<Decision[]>;
  
  // Sync operations
  triggerSync: () => Promise<void>;
  getSyncStatus: () => Promise<SyncStatus>;
  
  // Utility
  validateDecision: (decision: Partial<Decision>) => ReturnType<typeof validationService.validateDecision>;
  duplicateDecision: (id: string) => Promise<Decision>;
}

export function useDecisions(options: UseDecisionsOptions = {}): UseDecisionsReturn {
  const {
    autoSync = true,
    syncInterval = 5, // 5 minutes
    enableOffline = true
  } = options;

  // State
  const [decisions, setDecisions] = useState<Decision[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [syncStatus, setSyncStatus] = useState<SyncStatus | null>(null);
  const [pendingSyncCount, setPendingSyncCount] = useState(0);

  // Refs for cleanup
  const syncIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const syncCallbackRef = useRef<(() => void) | null>(null);

  // Initialize and setup auto-sync
  useEffect(() => {
    if (enableOffline) {
      loadDecisions();
      updatePendingSyncCount();

      if (autoSync && syncInterval > 0) {
        syncIntervalRef.current = setInterval(() => {
          if (navigator.onLine) {
            triggerSync();
          }
        }, syncInterval * 60 * 1000);
      }

      // Listen for sync completion
      syncCallbackRef.current = offlineManager.onSyncComplete((result) => {
        updatePendingSyncCount();
        loadDecisions(); // Refresh after sync
        
        if (result.success) {
          setSyncStatus('synced');
          setError(null);
        } else {
          setSyncStatus('failed');
          setError(`Sync failed: ${result.errors.join(', ')}`);
        }
      });
    }

    return () => {
      if (syncIntervalRef.current) {
        clearInterval(syncIntervalRef.current);
      }
      if (syncCallbackRef.current) {
        syncCallbackRef.current();
      }
    };
  }, [autoSync, syncInterval, enableOffline]);

  // Update pending sync count
  const updatePendingSyncCount = useCallback(async () => {
    try {
      const count = await offlineManager.getPendingSyncCount();
      setPendingSyncCount(count);
    } catch (err) {
      console.error('Failed to get pending sync count:', err);
    }
  }, []);

  // Load decisions with optional filter
  const loadDecisions = useCallback(async (filter?: DecisionFilter) => {
    setLoading(true);
    setError(null);

    try {
      let loadedDecisions: Decision[];

      // Try to load from server first
      try {
        const queryParams = new URLSearchParams();
        if (filter?.statuses && filter.statuses.length > 0) queryParams.set('status', filter.statuses[0]);
        if (filter?.types && filter.types.length > 0) queryParams.set('type', filter.types[0]);
        if (filter?.riskLevels && filter.riskLevels.length > 0) queryParams.set('riskLevel', filter.riskLevels[0]);
        if (filter?.createdBy && filter.createdBy.length > 0) queryParams.set('createdBy', filter.createdBy[0]);
        // Note: limit is not part of DecisionFilter, would need to be passed separately
        queryParams.set('limit', '50'); // Default limit

        const response = await fetch('/api/audit/decisions?' + queryParams);

        if (response.ok) {
          loadedDecisions = await response.json();
          console.log('Decisions loaded from server');
        } else {
          throw new Error('Server unavailable');
        }
      } catch (serverError) {
        console.log('Server unavailable, loading from offline storage');
        // Fallback to offline storage
        loadedDecisions = await offlineManager.getAllDecisions(filter);
      }

      setDecisions(loadedDecisions);
    } catch (err: any) {
      console.error('Failed to load decisions:', err);
      setError(err.message);
      
      // Fallback to offline data if server fails
      if (!offlineManager.isOffline()) {
        try {
          const offlineDecisions = await offlineManager.getAllDecisions(filter);
          setDecisions(offlineDecisions);
          setError('Using offline data - sync when connection restored');
        } catch (offlineErr) {
          console.error('Failed to load offline decisions:', offlineErr);
        }
      }
    } finally {
      setLoading(false);
    }
  }, [enableOffline]);

  // Create new decision
  const createDecision = useCallback(async (
    decisionData: Omit<Decision, 'id' | 'createdAt' | 'updatedAt' | 'signature'>
  ): Promise<Decision> => {
    try {
      const now = new Date();
      const deviceId = await decisionCrypto.generateDeviceId();
      
      const decision: Decision = {
        ...decisionData,
        id: decisionCrypto.generateSecureId(),
        createdAt: now,
        updatedAt: now,
        deviceId,
        status: 'draft',
        syncStatus: 'pending',
        editHistory: [],
        signature: '', // Will be generated below
      };

      // Generate signature
      decision.signature = await decisionCrypto.generateDecisionSignature(decision);

      // Validate before saving
      const validationErrors = validationService.validateDecision(decision);
      if (validationErrors.length > 0) {
        throw new Error(`Validation failed: ${validationErrors.map(e => e.message).join(', ')}`);
      }

      // Save locally first (always available)
      await offlineManager.saveDecision(decision);

      // Try to sync to server if online
      try {
        const response = await fetch('/api/audit/decisions', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(decision)
        });

        if (response.ok) {
          const serverDecision = await response.json();
          decision.syncStatus = 'synced';
          // Update offline storage with sync status
          await offlineManager.saveDecision(decision);
          console.log('Decision synced to server successfully');
        }
      } catch (syncError) {
        console.log('Decision saved offline, will sync when online');
        // Decision is already saved offline, sync will happen later
      }

      // Update local state
      setDecisions(prev => [decision, ...prev]);
      updatePendingSyncCount();

      return decision;
    } catch (err: any) {
      console.error('Failed to create decision:', err);
      setError(err.message);
      throw err;
    }
  }, []);

  // Update existing decision
  const updateDecision = useCallback(async (
    id: string, 
    updates: Partial<Decision>
  ): Promise<Decision> => {
    try {
      const existingDecision = await offlineManager.getDecision(id);
      if (!existingDecision) {
        throw new Error('Decision not found');
      }

      const now = new Date();
      const updatedDecision: Decision = {
        ...existingDecision,
        ...updates,
        id, // Ensure ID doesn't change
        updatedAt: now,
        syncStatus: 'pending',
      };

      // Add to edit history
      Object.keys(updates).forEach(field => {
        if (field !== 'updatedAt' && field !== 'syncStatus' && field !== 'editHistory') {
          updatedDecision.editHistory.push({
            id: decisionCrypto.generateSecureId(),
            editedAt: now,
            editedBy: updatedDecision.createdBy, // In real app, get from auth context
            editedByName: updatedDecision.createdByName,
            field,
            oldValue: (existingDecision as any)[field],
            newValue: (updates as any)[field],
            reason: 'User edit' // Could be provided in updates
          });
        }
      });

      // Regenerate signature
      updatedDecision.signature = await decisionCrypto.generateDecisionSignature(updatedDecision);

      // Validate
      const validationErrors = validationService.validateDecision(updatedDecision);
      if (validationErrors.length > 0) {
        throw new Error(`Validation failed: ${validationErrors.map(e => e.message).join(', ')}`);
      }

      // Save
      await offlineManager.saveDecision(updatedDecision);

      // Update local state
      setDecisions(prev => prev.map(d => d.id === id ? updatedDecision : d));
      updatePendingSyncCount();

      return updatedDecision;
    } catch (err: any) {
      console.error('Failed to update decision:', err);
      setError(err.message);
      throw err;
    }
  }, []);

  // Delete decision
  const deleteDecision = useCallback(async (id: string): Promise<void> => {
    try {
      await offlineManager.deleteDecision(id);
      setDecisions(prev => prev.filter(d => d.id !== id));
      updatePendingSyncCount();
    } catch (err: any) {
      console.error('Failed to delete decision:', err);
      setError(err.message);
      throw err;
    }
  }, []);

  // Get single decision
  const getDecision = useCallback(async (id: string): Promise<Decision | null> => {
    try {
      return await offlineManager.getDecision(id);
    } catch (err: any) {
      console.error('Failed to get decision:', err);
      setError(err.message);
      return null;
    }
  }, []);

  // Refresh decisions
  const refreshDecisions = useCallback(async () => {
    await loadDecisions();
  }, [loadDecisions]);

  // Search decisions
  const searchDecisions = useCallback(async (searchTerm: string): Promise<Decision[]> => {
    try {
      const filter: DecisionFilter = { searchTerm };
      const results = await offlineManager.getAllDecisions(filter);
      return results;
    } catch (err: any) {
      console.error('Failed to search decisions:', err);
      setError(err.message);
      return [];
    }
  }, []);

  // Trigger manual sync
  const triggerSync = useCallback(async () => {
    if (offlineManager.isOffline()) {
      throw new Error('Cannot sync while offline');
    }

    try {
      setSyncStatus('pending');
      const result = await offlineManager.triggerSync();
      
      if (result.success) {
        setSyncStatus('synced');
        setError(null);
        await loadDecisions(); // Refresh after sync
      } else {
        setSyncStatus('failed');
        setError(`Sync failed: ${result.errors.join(', ')}`);
      }
    } catch (err: any) {
      console.error('Sync failed:', err);
      setSyncStatus('failed');
      setError(err.message);
      throw err;
    } finally {
      updatePendingSyncCount();
    }
  }, [loadDecisions]);

  // Get current sync status
  const getSyncStatus = useCallback(async (): Promise<SyncStatus> => {
    if (offlineManager.isOffline()) {
      return 'pending';
    }
    if (pendingSyncCount > 0) {
      return 'pending';
    }
    return 'synced';
  }, [pendingSyncCount]);

  // Validate decision
  const validateDecision = useCallback((decision: Partial<Decision>) => {
    return validationService.validateDecision(decision);
  }, []);

  // Duplicate decision
  const duplicateDecision = useCallback(async (id: string): Promise<Decision> => {
    const original = await getDecision(id);
    if (!original) {
      throw new Error('Decision not found');
    }

    const duplicate = await createDecision({
      ...original,
      title: `Copy of ${original.title}`,
      status: 'draft',
      evidence: [], // Don't copy evidence
      editHistory: [], // Fresh history
      createdBy: original.createdBy, // In real app, use current user
      createdByName: original.createdByName
    });

    return duplicate;
  }, [createDecision, getDecision]);

  return {
    // State
    decisions,
    loading,
    error,
    syncStatus,
    pendingSyncCount,
    
    // Actions
    createDecision,
    updateDecision,
    deleteDecision,
    getDecision,
    
    // List operations
    loadDecisions,
    refreshDecisions,
    searchDecisions,
    
    // Sync operations
    triggerSync,
    getSyncStatus,
    
    // Utility
    validateDecision,
    duplicateDecision
  };
}